/**
 * THE named, reusable recovery for a STALE `rfqBundle.draftRfqId`.
 *
 * **The defect this closes.** `draftRfqId` — the eagerly-minted server draft
 * id (ADR-0082) — is persisted in `sessionStorage` (ADR-0103 D1, keyed
 * `ff_rfq_bundle_v1`) so a refresh or a navigate-away/back resumes the same
 * draft instead of minting a second one. That id can therefore OUTLIVE the row
 * it names: the draft was abandoned/submitted from another tab, a TTL reaper
 * collected it (ADR-0107 D1), or — the common case in localdev — Postgres was
 * reset/re-seeded under a tab that still holds the snapshot. Every
 * `{rfqId}`-scoped call then 404s, and the server says so in the only words it
 * has: **"No such RFQ in this account"**.
 *
 * `handleSaveConfigure` created a draft only when `draftRfqId` was FALSY, so a
 * present-but-stale id was a permanent dead end: the Bulk configure modal's
 * "Save configuration" surfaced that server copy and there was no way forward
 * for the buyer — not a retry, not a reload (the id is persisted, so it comes
 * back), and no affordance anywhere on `/rfq/new` that clears it. This is
 * pre-existing, not caused by the finish-option work on this branch.
 *
 * **The fix.** A 404 on a `{rfqId}`-scoped compose-time call has exactly one
 * honest interpretation: *my persisted draft id is garbage.* So discard it,
 * re-run the create-draft path that already exists for the "no id yet" case
 * (which builds the draft from the CURRENT lines and metadata — nothing is
 * lost but the id itself), and retry the operation once against the new id.
 *
 * **Three rules this seam exists to enforce in one place**, because getting any
 * of them wrong at a call site is a worse bug than the one being fixed:
 *  1. **404 ONLY**, detected from the RFC 7807 `Problem.status` the API always
 *     sends (`error?.status`, the same convention `ensure-lines-on-server-draft.ts`
 *     and `ManageUsersPage`/`CartPage` already read) — NEVER by matching the
 *     detail copy, which is server-owned and may change. Recreating the draft on
 *     a 400 (the specification was rejected) or a 409 (the append seam's own
 *     reconcile case) would throw away a live draft in response to a problem
 *     that had nothing to do with its identity.
 *  2. **ONCE.** If the retry also reports a stale draft, that is reported —
 *     there is no second recreate and no loop, for the same reason
 *     `ensureLinesOnServerDraft`'s reconcile is a single step: an endless
 *     create-retry cycle against a broken server would mint orphan drafts as
 *     fast as the network allows.
 *  3. **Other failures pass through verbatim** — this wrapper never re-labels
 *     somebody else's error message.
 *
 * ADR-0117 D11's standing rule applies here too: this is a NAMED function, not
 * inlined logic, so the next `{rfqId}`-scoped compose-time operation calls it
 * rather than re-deriving it. Today's callers are `RfqReviewPage`'s
 * `handleSaveConfigure` (Configure) and `handleSubmit` (Submit) — see
 * `stale-draft-recovery.test.ts` for the rules above, pinned.
 */

/** The RFC 7807 `Problem` shape any openapi-fetch error branch carries, reduced
 * to the one field this module reads. */
export interface ProblemLike {
  readonly status?: number
}

/**
 * True when a failed `{rfqId}`-scoped call means "this draft id no longer
 * names anything". Reads the response STATUS, never the `detail` copy.
 */
export function isStaleDraftProblem(error: ProblemLike | null | undefined): boolean {
  return error?.status === 404
}

/** One attempt at a `{rfqId}`-scoped operation. `staleDraft` is the ONLY signal
 * {@link withFreshDraftRetry} acts on — a caller sets it from
 * {@link isStaleDraftProblem}, so the status check lives in one place. */
export type DraftAttempt<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly message: string; readonly staleDraft?: boolean }

/** The outcome of re-running the create-draft path. */
export type FreshDraft =
  | { readonly ok: true; readonly rfqId: string }
  | { readonly ok: false; readonly message: string }

/**
 * Runs `attempt` against `rfqId`; if (and only if) it reports a stale draft,
 * mints a fresh one via `createFreshDraft` and runs `attempt` ONCE more against
 * the new id. Every other outcome — success, a non-stale failure, a failed
 * recreate, or a retry that is still stale — is returned as-is.
 *
 * `createFreshDraft` owns discarding the stale id (so it is gone even if the
 * create then fails) and recording the new draft's server state; this function
 * owns only the when-and-how-many-times.
 */
export async function withFreshDraftRetry<T>(
  rfqId: string,
  attempt: (rfqId: string) => Promise<DraftAttempt<T>>,
  createFreshDraft: () => Promise<FreshDraft>,
): Promise<DraftAttempt<T>> {
  const first = await attempt(rfqId)
  if (first.ok || !first.staleDraft) {
    return first
  }
  const fresh = await createFreshDraft()
  if (!fresh.ok) {
    return { ok: false, message: fresh.message }
  }
  // EXACTLY one retry. Whatever this returns is final — including another
  // `staleDraft` failure, which is reported rather than recovered again.
  return attempt(fresh.rfqId)
}
